import { HttpClientModule } from "@angular/common/http";
import { DoBootstrap, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { NgxsFormPluginModule } from "@ngxs/form-plugin";
import { cookieInterceptorProvider, ElementRoute, ElementsModule, PipesModule } from "@vattenfall/core";
import { SelfServiceModule } from "@vattenfall/self-service";
import { UiModule } from "@vattenfall/ui";
import { environment } from "src/environments/environment";

import { finnishProductDetailsServiceMockupProvider } from "./flows/shared/services/finnish-product-details.service.mockup";
import { NgxsConfigModule } from "./ngxs-config.module";
import { StaticLoadingModule } from "./samples/static-loading.module";
import { TailwindCssModule } from "./samples/tailwind-css.module";

/**
 * This is an example of how Vattenfall Elements should be loaded according
 * to the next version of the `ElementsModule`,
 */
const EXAMPLE_ROUTES = environment.production
  ? []
  : ([
      /**
       * This module will be loaded when the element is present on a page
       */
      {
        tagName: "lazy-loading",
        loadChildren: () => import("./samples/lazy-loading.module").then((m) => m.LazyLoadingModule),
      },
      /**
       * This module will be loaded when the application bootstraps
       */
      {
        tagName: "static-loading",
        elementModule: StaticLoadingModule,
      },
      /**
       * This module is used for tesing Tailwind configs
       */
      {
        tagName: "tailwind-css",
        elementModule: TailwindCssModule,
      },
    ] as ElementRoute[]);

const LAZY_ROUTES = [
  {
    tagName: "my-widgets-premise-details",
    loadChildren: () =>
      import("./widgets/my-widgets-premise-details/my-widgets-premise-details.module").then(
        (m) => m.MyWidgetsPremiseDetailsModule
      ),
  },
  {
    tagName: "my-widgets-invoice-overview",
    loadChildren: () =>
      import("./widgets/my-widgets-invoice-overview/my-widgets-invoice-overview.module").then(
        (m) => m.MyWidgetsInvoiceOverviewModule
      ),
  },
  {
    tagName: "my-widgets-consumption-overview",
    loadChildren: () =>
      import("./widgets/my-widgets-consumption-overview/my-widgets-consumption-overview.module").then(
        (m) => m.MyWidgetsConsumptionOverviewModule
      ),
  },
  {
    tagName: "my-widgets-electricity-price-now",
    loadChildren: () =>
      import("./widgets/my-widgets-electricity-price-now/my-widgets-electricity-price-now.module").then(
        (m) => m.MyWidgetsElectricityPriceNowModule
      ),
  },
  {
    tagName: "my-widgets-contract-price",
    loadChildren: () =>
      import("./widgets/my-widgets-contract-price/my-widgets-contract-price.module").then(
        (m) => m.MyWidgetsContractPriceModule
      ),
  },
  {
    tagName: "my-widgets-static-light-yellow",
    loadChildren: () =>
      import("./widgets/my-widgets-static-light-yellow/my-widgets-static-light-yellow.module").then(
        (m) => m.MyWidgetsStaticLightYellowModule
      ),
  },
  {
    tagName: "my-widgets-static-white",
    loadChildren: () =>
      import("./widgets/my-widgets-static-white/my-widgets-static-white.module").then(
        (m) => m.MyWidgetsStaticWhiteModule
      ),
  },
  {
    tagName: "my-widgets-static-yellow",
    loadChildren: () =>
      import("./widgets/my-widgets-static-yellow/my-widgets-static-yellow.module").then(
        (m) => m.MyWidgetsStaticYellowModule
      ),
  },
  {
    tagName: "elements-input",
    loadChildren: () => import("./elements/input/input.module").then((m) => m.InputModule),
  },
  {
    tagName: "elements-dialog",
    loadChildren: () => import("./elements/dialog/dialog.module").then((m) => m.DialogModule),
  },
  {
    tagName: "flows-invoice-method-section",
    loadChildren: () =>
      import("./flows/flows-invoice-method-section/flows-invoice-method-section.module").then(
        (m) => m.FlowsInvoiceMethodSectionModule
      ),
  },
  {
    tagName: "flows-product-select-section",
    loadChildren: () =>
      import("./flows/flows-product-select-section/flows-product-select-section.module").then(
        (m) => m.FlowsProductSelectSectionModule
      ),
  },
  {
    tagName: "flows-product-select-option",
    loadChildren: () =>
      import(
        "./flows/flows-product-select-section/flows-product-select-option/flows-product-select-option.module"
      ).then((m) => m.FlowsProductSelectOptionModule),
  },
  {
    tagName: "flows-move-business-sweden-checkout",
    loadChildren: () =>
      import("./flows/flows-move-business-sweden-checkout/flows-move-business-sweden-checkout.module").then(
        (m) => m.FlowsMoveBusinessSwedenCheckoutModule
      ),
  },
  {
    tagName: "flows-new-contract-details-section",
    loadChildren: () =>
      import("./flows/flows-new-contract-details-section/flows-new-contract-details-section.module").then(
        (m) => m.FlowsNewContractDetailsSectionModule
      ),
  },
  {
    tagName: "flows-new-contract-section",
    loadChildren: () =>
      import("./flows/flows-new-contract-section/flows-new-contract-section.module").then(
        (m) => m.FlowsNewContractSectionModule
      ),
  },
  {
    tagName: "flows-submit-contract-section",
    loadChildren: () =>
      import("./flows/flows-submit-contract-section/flows-submit-contract-section.module").then(
        (m) => m.FlowsSubmitContractSectionModule
      ),
  },
  {
    tagName: "flows-premises-select",
    loadChildren: () =>
      import("./flows/flows-premises-select/flows-premises-select.module").then((m) => m.FlowsPremisesSelectModule),
  },
  {
    tagName: "flows-submit-renewal-section",
    loadChildren: () =>
      import("./flows/flows-submit-renewal-section/flows-submit-renewal-section.module").then(
        (m) => m.FlowsSubmitRenewalSectionModule
      ),
  },
  {
    tagName: "flows-change-contract-sweden-checkout",
    loadChildren: () =>
      import("./flows/flows-change-contract-sweden-checkout/flows-change-contract-sweden-checkout.module").then(
        (m) => m.FlowsChangeContractSwedenCheckoutModule
      ),
  },
  {
    tagName: "flows-change-contract-sweden-confirmation",
    loadChildren: () =>
      import("./flows/flows-change-contract-sweden-confirmation/flows-change-contract-sweden-confirmation.module").then(
        (m) => m.FlowsChangeContractSwedenConfirmationModule
      ),
  },
  {
    tagName: "flows-customer-details-section",
    loadChildren: () =>
      import("./flows/flows-customer-details-section/flows-customer-details-section.module").then(
        (m) => m.FlowsCustomerDetailsSectionModule
      ),
  },
  {
    tagName: "flows-confirmation-renewal-section",
    loadChildren: () =>
      import("./flows/flows-confirmation-renewal-section/flows-confirmation-renewal-section.module").then(
        (m) => m.FlowsConfirmationRenewalSectionModule
      ),
  },
  {
    tagName: "flows-confirmation-contract-section",
    loadChildren: () =>
      import("./flows/flows-confirmation-contract-section/flows-confirmation-contract-section.module").then(
        (m) => m.FlowsConfirmationContractSectionModule
      ),
  },
  {
    tagName: "flows-contract-details-section",
    loadChildren: () =>
      import("./flows/flows-contract-details-section/flows-contract-details-section.module").then(
        (m) => m.FlowsContractDetailsSectionModule
      ),
  },
  {
    tagName: "flows-premise-contract-summary",
    loadChildren: () =>
      import("./flows/flows-premise-contract-summary/flows-premise-contract-summary.module").then(
        (m) => m.FlowsPremiseContractSummaryModule
      ),
  },
  {
    tagName: "flows-move-business-sweden-confirmation",
    loadChildren: () =>
      import("./flows/flows-move-business-sweden-confirmation/flows-move-business-sweden-confirmation.module").then(
        (m) => m.FlowsMoveBusinessSwedenConfirmationModule
      ),
  },
  {
    tagName: "flows-acquisition-business-sweden-confirmation",
    loadChildren: () =>
      import(
        "./flows/flows-acquisition-business-sweden-confirmation/flows-acquisition-business-sweden-confirmation.module"
      ).then((m) => m.FlowsAcquisitionBusinessSwedenConfirmationModule),
  },
  {
    tagName: "flows-acquisition-private-sweden-checkout",
    loadChildren: () =>
      import("./flows/flows-acquisition-private-sweden-checkout/flows-acquisition-private-sweden-checkout.module").then(
        (m) => m.FlowsAcquisitionPrivateSwedenCheckoutModule
      ),
  },
  {
    tagName: "flows-move-private-sweden-checkout",
    loadChildren: () =>
      import("./flows/flows-move-private-sweden-checkout/flows-move-private-sweden-checkout.module").then(
        (m) => m.FlowsMovePrivateSwedenCheckoutModule
      ),
  },
  {
    tagName: "flows-acquisition-private-sweden-confirmation",
    loadChildren: () =>
      import(
        "./flows/flows-acquisition-private-sweden-confirmation/flows-acquisition-private-sweden-confirmation.module"
      ).then((m) => m.FlowsAcquisitionPrivateSwedenConfirmationModule),
  },
  {
    tagName: "flows-move-private-sweden-confirmation",
    loadChildren: () =>
      import("./flows/flows-move-private-sweden-confirmation/flows-move-private-sweden-confirmation.module").then(
        (m) => m.FlowsMovePrivateSwedenConfirmationModule
      ),
  },
  {
    tagName: "elements-postalcode-gridarea",
    loadChildren: () =>
      import("./elements/postalcode-gridarea/postalcode-gridarea.module").then((m) => m.PostalcodeGridareaModule),
  },
  {
    tagName: "elements-postalcode-gridarea-consumption-redirect",
    loadChildren: () =>
      import(
        "./elements/postalcode-gridarea-consumption-redirect/postalcode-gridarea-consumption-redirect.module"
      ).then((m) => m.PostalcodeGridareaConsumptionRedirectModule),
  },
  {
    tagName: "my-select-electrical-product",
    loadChildren: () =>
      import("./flows/my-select-electricity-product/my-select-electricity-product.module").then(
        (m) => m.MySelectElectricityProductModule
      ),
  },
  {
    tagName: "my-select-consumption-interval-form",
    loadChildren: () =>
      import("./flows/my-select-consumption-interval-form/my-select-consumption-interval-form.module").then(
        (m) => m.MySelectConsumptionIntervalFormModule
      ),
  },
  {
    tagName: "flows-product-details-section",
    loadChildren: () =>
      import("./flows/flows-product-details-section/flows-product-details-section.module").then(
        (m) => m.FlowsProductDetailsSectionModule
      ),
  },
  {
    tagName: "flows-fi-renewal-product-select-section",
    loadChildren: () =>
      import("./flows/flows-fi-renewal-product-select-section/flows-fi-renewal-product-select-section.module").then(
        (m) => m.FlowsFIRenewalProductSelectSectionModule
      ),
  },
  {
    tagName: "flows-fi-renewal-product-select-option",
    loadChildren: () =>
      import(
        "./flows/flows-fi-renewal-product-select-section/flows-fi-renewal-product-select-option/flows-fi-renewal-product-select-option.module"
      ).then((m) => m.FlowsFIRenewalProductSelectOptionModule),
  },
  {
    tagName: "flows-fi-renewal-checkout",
    loadChildren: () =>
      import("./flows/flows-fi-renewal-checkout/flows-fi-renewal-checkout.module").then(
        (m) => m.FlowsFiRenewalCheckoutModule
      ),
  },
  {
    tagName: "flows-fi-renewal-contact-section",
    loadChildren: () =>
      import("./flows/flows-fi-renewal-contact-section/flows-fi-renewal-contact-section.module").then(
        (m) => m.FlowsFiRenewalContactSectionModule
      ),
  },
  {
    tagName: "flows-fi-renewal-confirmation",
    loadChildren: () =>
      import("./flows/flows-fi-renewal-confirmation/flows-fi-renewal-confirmation.module").then(
        (m) => m.FlowsFiRenewalConfirmationModule
      ),
  },
  {
    tagName: "flows-fi-renewal-login",
    loadChildren: () =>
      import("./flows/flows-fi-renewal-login/flows-fi-renewal-login.element.module").then(
        (m) => m.FlowsFIRenewalLoginModule
      ),
  },
  {
    tagName: "electrical-products-filter",
    loadChildren: () =>
      import("./flows/electrical-products-filter/electrical-products-filter.module").then(
        (m) => m.ElectricalProductsFilterModule
      ),
  },
  {
    tagName: "my-contact-notification-dialog",
    loadChildren: () =>
      import("./profile/my-contact-notification-dialog/my-contact-notification-dialog.module").then(
        (m) => m.MyContactNotificationDialogModule
      ),
  },
  {
    tagName: "my-profile-communication-settings",
    loadChildren: () =>
      import("./profile/my-profile-communication-settings/my-profile-communication-settings.module").then(
        (m) => m.MyProfileCommunicationSettingsModule
      ),
  },
  {
    tagName: "my-profile-detail-view",
    loadChildren: () =>
      import("./profile/my-profile-detail-view/my-profile-detail-view.module").then((m) => m.MyProfileDetailViewModule),
  },
  {
    tagName: "elements-energy-source-banner",
    loadChildren: () =>
      import("./elements/energy-source-banner/energy-source-banner.module").then((m) => m.EnergySourceBannerModule),
  },
  {
    tagName: "elements-energy-source-submitter",
    loadChildren: () =>
      import("./elements/energy-source-submitter/energy-source-submitter.module").then(
        (m) => m.EnergySourceSubmitterModule
      ),
  },
  {
    tagName: "flows-acquisition-business-sweden-checkout",
    loadChildren: () =>
      import(
        "./flows/flows-acquisition-business-sweden-checkout/flows-acquisition-business-sweden-checkout.module"
      ).then((m) => m.FlowsAcquisitionBusinessSwedenCheckoutModule),
  },
  {
    tagName: "flows-move-acquisition-switch",
    loadChildren: () =>
      import("./flows/flows-move-acquisition-switch/flows-move-acquisition-switch.module").then(
        (m) => m.FlowsMoveAcquisitionSwitchModule
      ),
  },
  {
    tagName: "flows-move-acquisition-private-switch",
    loadChildren: () =>
      import("./flows/flows-move-acquisition-private-switch/flows-move-acquisition-private-switch.module").then(
        (m) => m.FlowsMoveAcquisitionPrivateSwitchModule
      ),
  },
  {
    tagName: "my-contracts-view",
    loadChildren: () =>
      import("./contracts/my-contracts-view/my-contracts-view.module").then((m) => m.MyContractsViewModule),
  },
  {
    tagName: "flows-product-addons-section",
    loadChildren: () =>
      import("./flows/flows-product-addons-section/flows-product-addons-section.module").then(
        (m) => m.FlowsProductAddonsSectionModule
      ),
  },
  {
    tagName: "heat-calculator-b2c-calculator",
    loadChildren: () =>
      import("./heat-calculator/b2c-calculator/b2c-calculator.module").then((m) => m.B2cCalculatorModule),
  },
  {
    tagName: "heat-calculator-area-selector",
    loadChildren: () =>
      import("./heat-calculator/area-selector/area-selector.module").then((m) => m.AreaSelectorModule),
  },
  {
    tagName: "heat-calculator-price-section",
    loadChildren: () =>
      import("./heat-calculator/price-section/price-section.module").then((m) => m.PriceSectionModule),
  },
  {
    tagName: "heat-calculator-b2b-input",
    loadChildren: () =>
      import("./heat-calculator/b2b-calculator-input/b2b-calculator-input.module").then(
        (m) => m.B2bCalculatorInputModule
      ),
  },
  {
    tagName: "heat-calculator-b2b-result-section",
    loadChildren: () =>
      import("./heat-calculator/b2b-calculator-result-section/b2b-calculator-result-section.module").then(
        (m) => m.B2bCalculatorResultSectionModule
      ),
  },
];
@NgModule({
  imports: [
    BrowserModule,
    HttpClientModule,
    NgxsConfigModule,
    UiModule,
    PipesModule,
    NgxsFormPluginModule.forRoot(),
    SelfServiceModule.forRoot({
      mockup: !environment.production,
    }),
    ElementsModule.forRoot([...EXAMPLE_ROUTES, ...LAZY_ROUTES]),
  ],
  providers: [cookieInterceptorProvider, finnishProductDetailsServiceMockupProvider],
})
export class AppModule implements DoBootstrap {
  ngDoBootstrap() {}
}
